import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";

const logger = LazyLoadingSectionLogger.create(LoggingSection.filters);

//#region BaseStructures
/**
 * Bázová třída pro kontrolu schématu objektů
 */
export abstract class IBaseDynamicSchemaInterfaceClass<T extends object> {
    public readonly requiredFields?: IFieldType<T>[];
    public readonly optionalFields?: IFieldType<T>[];
}

/**
 * Základní datové typy fieldů
 */
export enum FieldType {
    boolean,
    number,
    string,
    array,
    object,
    any,
}

/**
 * Datový interface s informacemi o fieldu
 */
export interface IFieldType<T extends object> {
    name: keyof(T);
    type: FieldType;
    allowedValues?: any[]; // TODO: any? Mám jak jinak určit typ?
    caseInsensitiveAllowedValues?: boolean;
}

/**
 * Typy validačních chyb
 */
export enum SchemaValidationErrorType {
    missingField,
    invalidDataType,
    notAllowedValue,
}

/**
 * Typy validačních varování
 */
export enum SchemaValidationWarningType {
    unsupportedField,
}

/**
 * Validační chyba
 */
export interface ISchemaValidationError<T extends object> {
    field: IFieldType<T>;
    errorType: SchemaValidationErrorType;
    value?: any;
}

/**
 * Validační varování
 */
export interface ISchemaValidationWarning {
    fieldName: PropertyKey;
    warningType: SchemaValidationWarningType;
    value?: any;
}

/**
 * Výsledek validace
 */
export interface ISchemaValidationResult<T extends object> {
    /** Příznak, zda byla validace úspěšná */
    success: boolean;
    /** Validační chyby */
    errors?: ISchemaValidationError<T>[];
    /** Validační varování */
    warnings?: ISchemaValidationWarning[];
}

//#endregion BaseStructures

/**
 * Zvaliduje objekt proti definovanému schématu.
 * Schéma musí dědit z IBaseDynamicSchemaInterfaceClass. Na konci souboru jsou příklady použití.
 */
export class ObjectSchemaValidator {
    /**
     * Zvaliduje objekt proti definovanému schématu.
     * @param object Objekt který se má validovat
     * @param schema Schéma proti kterému se má validovat
     */
    public static validateObjectSchema<T extends object>(object: object, schema: IBaseDynamicSchemaInterfaceClass<T>, logErrors = false): ISchemaValidationResult<T> /*TODO: nevracet jen bool*/ {
        const result: ISchemaValidationResult<T> = {
            success: true,
            errors: [],
            warnings: [],
        };

        const objectFields = new Set(Object.keys(object));

        if (!schema.requiredFields) {
            result.success = false; // TODO: přidat hlášku o neexistujících povinných ve schématu? Nebo povolovat schéma bez povinných?
            return result; // TODO: nevracet jen bool
        }

        const validate = (field: IFieldType<T>, predicate: (propertyValue: any) => boolean, propertyType: string, allowNull: boolean = false): boolean => {
            const propertyName = field.name;

            if ((object as any)[propertyName] == null) {
                if (!allowNull) {
                    if (logErrors)
                        logger.error(`Undefined propertyName '${String(propertyName)}'.`);
                    return false;
                }
            } else if (!predicate((object as any)[propertyName])) {
                if (logErrors)
                    logger.error(`Field '${String(propertyName)}' is not ${propertyType}.`);
                return false;
            }

            return true;
        };

        const validateString = (field: IFieldType<T>, allowNull: boolean = false): boolean => {
            return validate(field, (val) => typeof val === "string", "string", allowNull);
        };
        const validateArray = (field: IFieldType<T>, allowNull: boolean = false): boolean => {
            return validate(field, (val) => Array.isArray(val), "array", allowNull);
        };
        const validateBoolean = (field: IFieldType<T>, allowNull: boolean = false): boolean => {
            return validate(field, (val) => typeof val === "boolean", "boolean", allowNull);
        };
        const validateObject = (field: IFieldType<T>, allowNull: boolean = false): boolean => {
            return validate(field, (val) => typeof val === "object", "object", allowNull);
        };
        const validateNumber = (field: IFieldType<T>, allowNull: boolean = false): boolean => {
            return validate(field, (val) => typeof val === "number", "number", allowNull);
        };
        //const validateNonNegativeNumber = (property: string, allowNull: boolean = false) => {
        //    validate(property, (val) => typeof val === "number" && val >= 0, "positive number or zero", allowNull);
        //};

        const validateField = (field: IFieldType<T>): boolean => {
            let successResult = true;

            switch (field.type) {
                case FieldType.boolean:
                    successResult = validateBoolean(field, false);
                    break;
                case FieldType.number:
                    successResult = validateNumber(field, false);
                    break;
                case FieldType.string:
                    successResult = validateString(field, false);
                    break;
                case FieldType.array:
                    successResult = validateArray(field, false);
                    break;
                case FieldType.object:
                    successResult = validateObject(field, false);
                    break;
                case FieldType.any: // Projde validací vždy
                default:
            }

            if (!successResult) {
                result.errors.push({ field: field, errorType: SchemaValidationErrorType.invalidDataType, value: (object as any)[field.name] });
            } else {
                // TODO: kontrola zatím nefunguje pro pole
                if (field.allowedValues) {
                    let validValue = true;

                    if (field.caseInsensitiveAllowedValues)
                        validValue = field.allowedValues.some((allowedValue) => allowedValue.toLocaleUpperCase() === (object as any)[field.name].toLocaleUpperCase());
                    else
                        validValue = field.allowedValues.includes((object as any)[field.name]);

                    if (!validValue) {
                        if (logErrors)
                            logger.error(`Fields's '${String(field.name)}' value '${(object as any)[field.name]}' is not among allowed values [${field.allowedValues}].`);
                        successResult = false;
                        result.errors.push({ field: field, errorType: SchemaValidationErrorType.notAllowedValue, value: (object as any)[field.name] });
                    }
                }
            }

            return successResult;
        };

        // Kontrola povinných polí, jejich typů a povolených hodnot
        for (const requiredField of schema.requiredFields) {
            if (typeof requiredField.name !== "string")
                continue;

            let success = true;

            if (!Object.keys(object).includes(requiredField.name)) {
                success = false;
                result.errors.push({ field: requiredField, errorType: SchemaValidationErrorType.missingField });
            }

            objectFields.delete(requiredField.name);

            if (success) {
                success = validateField(requiredField);
            }

            if (!success) {
                result.success = false;
            }
        }

        // Kontrola nepovinných polí, jejich typů a povolených hodnot
        for (const objectFieldName of objectFields) {
            const optionalField = schema.optionalFields.find((optionalFieldDefinition) => optionalFieldDefinition.name === objectFieldName);

            if (!optionalField) {
                result.warnings.push({ fieldName: objectFieldName, warningType: SchemaValidationWarningType.unsupportedField, value: (object as any)[objectFieldName] });
            } else {
                if (!validateField(optionalField)) {
                    result.success = false;
                }
            }
        }

        return result;
    }

    //#region TestMethods
    public static fii(): boolean {
        const testObject: any/*ITestSchemaInterface*/ = { requiredField: "a", requiredField2: { requiredSubField: 1, requiredSubField2: 2 }, sneakyField: "buhehehe!" };

        if (!ObjectSchemaValidator.validateObjectSchema(testObject, ITestSchemaInterface).success) // Projde s warningem
            return false;

        if (!ObjectSchemaValidator.validateObjectSchema(testObject.requiredField2, ITestSchemaSubInterface).success) // Projde
            return false;

        return true;
    }

    public static fuu(): boolean {
        //const testObject: any = { requiredField: "a" }; // První, ani druhá validace neprojde
        //const testObject: any = { requiredField: "b" }; // První, ani druhá validace neprojde
        const testObject: any = { requiredField: "nee" }; // První, ani druhá validace neprojde

        if (!ObjectSchemaValidator.validateObjectSchema(testObject, ITestSchemaInterface).success)
            return false;

        if (!ObjectSchemaValidator.validateObjectSchema(testObject.requiredField2, ITestSchemaSubInterface).success)
            return false;

        return true;
    }

    public static fuu2(): boolean {
        const testObject = { requiredField: "a", requiredField2: { requiredSubField: 1 } };

        if (!ObjectSchemaValidator.validateObjectSchema(testObject, ITestSchemaInterface).success) // Projde
            return false;

        if (!ObjectSchemaValidator.validateObjectSchema(testObject.requiredField2, ITestSchemaSubInterface).success) // Neprojde
            return false;

        return true;
    }
    //#endregion TestMethods
}

//#region ExampleInterfaces
interface ITestSchemaInterface {
    requiredField: string;
    requiredField2: ITestSchemaSubInterface;

    optionalField?: string;
}

class ITestSchemaInterface extends IBaseDynamicSchemaInterfaceClass<ITestSchemaInterface> {
    public static readonly requiredFields: (IFieldType<ITestSchemaInterface>)[] = [
        { name: "requiredField", type: FieldType.string, allowedValues: ["a", "b"] },
        { name: "requiredField2", type: FieldType.object },
    ];

    public static readonly optionalFields: (IFieldType<ITestSchemaInterface>)[] = [{ name: "optionalField", type: FieldType.string }];
}

interface ITestSchemaSubInterface {
    requiredSubField: number;
    requiredSubField2: number;
}

class ITestSchemaSubInterface extends IBaseDynamicSchemaInterfaceClass<ITestSchemaSubInterface> {
    public static readonly requiredFields: (IFieldType<ITestSchemaSubInterface>)[] = [
        { name: "requiredSubField", type: FieldType.number },
        { name: "requiredSubField2", type: FieldType.number },
    ];
    public static readonly optionalFields: (IFieldType<ITestSchemaSubInterface>)[] = [];
}
//#endregion ExampleInterfaces
